import type { RumViewEvent } from '@datadog/browser-rum'

/** Sticky vitals (FCP/LCP/TTFB): skip repeat forwards for the same view + vital. */
const sentByView = new Map<string, Set<string>>()
/** Evolving vitals (CLS/INP): skip only when the normalized value is unchanged. */
const lastSentValueByView = new Map<string, number>()
/** Views already finalized (view-end); ignore stray duplicate view-end updates. */
const completedViews = new Set<string>()

export function shouldIgnoreViewUpdate(viewId: string): boolean {
  return completedViews.has(viewId)
}

export function isViewEndEvent(event: RumViewEvent): boolean {
  return event.view.is_active === false
}

/** For vitals whose value is stable once reported (Datadog metric-update duplicates). */
export function emitWebVitalOnce(viewId: string, vital: string, fn: () => void): void {
  const seen = sentByView.get(viewId) ?? new Set<string>()
  if (seen.has(vital)) {
    return
  }
  seen.add(vital)
  sentByView.set(viewId, seen)
  fn()
}

/** For vitals that can change during a view (CLS score, INP duration). */
export function emitWebVitalIfValueChanged(viewId: string, vital: string, value: number, fn: () => void): void {
  const key = `${viewId}:${vital}`
  if (lastSentValueByView.get(key) === value) {
    return
  }
  lastSentValueByView.set(key, value)
  fn()
}

export function finalizeView(viewId: string): void {
  completedViews.add(viewId)
  sentByView.delete(viewId)
  for (const key of lastSentValueByView.keys()) {
    if (key.startsWith(`${viewId}:`)) {
      lastSentValueByView.delete(key)
    }
  }
}
